import { projectApi } from '@/api/project';
import { updateElementInHtml, type ElementContentUpdates } from '@/lib/domUtils';
import { resolveEditableDraft } from '@/lib/draftUtils';
import type { SelectedElementInfo } from '@/store/useCreatorStore';
import type { WebpageDraft } from '@/types/project';

export const SELECTED_ELEMENT_TARGETED_EDIT_MARKER = 'REQUEST SCOPE: LOCAL_ELEMENT_UPDATE';
export const EXISTING_PAGE_UPDATE_MARKER = 'TASK MODE: EXISTING_PAGE_UPDATE';

export type DominantLanguage = 'English' | 'Chinese' | 'Mixed' | 'Unknown';

type UnexpectedGlobalLanguageDrift = {
  beforeLanguage: DominantLanguage;
  afterLanguage: DominantLanguage;
  changedTextCount: number;
  totalTextCount: number;
};

const CJK_CHAR_REGEX = /[\u3400-\u9FFF\uF900-\uFAFF]/g;
const LATIN_CHAR_REGEX = /[A-Za-z]/g;
const MIN_TEXT_NODE_LENGTH = 2;
const MIN_LANGUAGE_SIGNAL_COUNT = 24;
const LANGUAGE_DOMINANCE_RATIO = 1.8;
const MIN_DRIFT_TEXT_NODE_COUNT = 12;
const MIN_CHANGED_TEXT_NODE_COUNT = 10;
const MIN_CHANGED_TEXT_NODE_RATIO = 0.6;

const normalizeText = (value?: string | null) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const countRegexMatches = (value: string, regex: RegExp) => value.match(regex)?.length ?? 0;

const inferDominantLanguageFromText = (value: string): DominantLanguage => {
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) return 'Unknown';

  const cjkCount = countRegexMatches(normalizedValue, CJK_CHAR_REGEX);
  const latinCount = countRegexMatches(normalizedValue, LATIN_CHAR_REGEX);

  if (cjkCount < MIN_LANGUAGE_SIGNAL_COUNT && latinCount < MIN_LANGUAGE_SIGNAL_COUNT) {
    return 'Unknown';
  }

  if (cjkCount >= MIN_LANGUAGE_SIGNAL_COUNT && cjkCount > latinCount * LANGUAGE_DOMINANCE_RATIO) {
    return 'Chinese';
  }

  if (latinCount >= MIN_LANGUAGE_SIGNAL_COUNT && latinCount > cjkCount * LANGUAGE_DOMINANCE_RATIO) {
    return 'English';
  }

  if (cjkCount > 0 && latinCount > 0) {
    return 'Mixed';
  }

  return cjkCount > 0 ? 'Chinese' : latinCount > 0 ? 'English' : 'Unknown';
};

const extractMeaningfulTextNodes = (html: string) => {
  const normalizedHtml = String(html || '').trim();
  if (!normalizedHtml) return [] as string[];

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(normalizedHtml, 'text/html');
    const root = doc.body || doc.documentElement;
    if (!root) return [];

    root.querySelectorAll('script, style, noscript').forEach((node) => node.remove());

    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const texts: string[] = [];

    while (walker.nextNode()) {
      const currentNode = walker.currentNode;
      const parentElement = currentNode.parentElement;
      if (!parentElement) continue;
      if (['SCRIPT', 'STYLE', 'NOSCRIPT', 'SVG'].includes(parentElement.tagName)) continue;

      const normalizedText = normalizeText(currentNode.textContent);
      if (normalizedText.length < MIN_TEXT_NODE_LENGTH) continue;
      texts.push(normalizedText);
    }

    return texts;
  } catch {
    return normalizedHtml
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<noscript[\s\S]*?<\/noscript>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .split(/\s{2,}|\n+/)
      .map((item) => normalizeText(item))
      .filter((item) => item.length >= MIN_TEXT_NODE_LENGTH);
  }
};

const countChangedTextNodes = (beforeTexts: string[], afterTexts: string[]) => {
  const sharedLength = Math.min(beforeTexts.length, afterTexts.length);
  let changedCount = Math.abs(beforeTexts.length - afterTexts.length);

  for (let index = 0; index < sharedLength; index += 1) {
    if (beforeTexts[index] !== afterTexts[index]) {
      changedCount += 1;
    }
  }

  return changedCount;
};

export const inferPagePrimaryLanguageFromHtml = (html?: string | null) =>
  inferDominantLanguageFromText(extractMeaningfulTextNodes(String(html || '')).join(' '));

export const buildSelectedElementFocusedRequest = (
  selectedElement: SelectedElementInfo,
  request: string,
  options?: {
    pageHtml?: string;
  },
) => {
  void options;
  const trimmedRequest = String(request || '').trim();
  const selectionNodes = selectedElement.selectionNodes?.length
    ? selectedElement.selectionNodes
    : [{ id: selectedElement.id, nodeName: selectedElement.nodeName, kind: 'self' as const }];
  const primaryNode = selectionNodes.find((node) => node.kind === 'self') ?? selectionNodes[0];
  const nodeName = primaryNode?.nodeName || selectedElement.nodeName || 'element';
  const targetId = primaryNode?.id || selectedElement.id;
  const className = primaryNode?.className?.split(/\s+/).find(Boolean);
  const elementLabel = [
    `<${nodeName.toLowerCase()}`,
    targetId ? ` id="${targetId}"` : className ? ` class="${className}"` : '',
    '>',
  ].join('');
  const currentText = normalizeText(selectedElement.text);

  return [
    SELECTED_ELEMENT_TARGETED_EDIT_MARKER,
    `Target element: ${elementLabel}`,
    currentText ? `Current element text: ${JSON.stringify(currentText)}` : '',
    `User request: ${trimmedRequest}`,
  ]
    .filter(Boolean)
    .join('\n');
};

export const buildExistingPageFocusedRequest = (
  request: string,
  options?: {
    pageHtml?: string;
  },
) => {
  void options;
  return String(request || '').trim();
};

export const buildExpertReviewApplyFocusedRequest = (
  request: string,
  options?: {
    pageHtml?: string;
  },
) => {
  void options;
  return String(request || '').trim();
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getDraftErrorCode = (error: unknown) => {
  const typedError = error as
    | {
        errorCode?: unknown;
        apiError?: { errorCode?: unknown; payload?: { errorCode?: unknown } };
      }
    | null
    | undefined;

  return String(
    typedError?.errorCode ||
      typedError?.apiError?.errorCode ||
      typedError?.apiError?.payload?.errorCode ||
      '',
  );
};

const isDraftVersionConflictError = (error: unknown) => {
  const message =
    error instanceof Error
      ? error.message
      : String((error as { message?: string } | null | undefined)?.message || error || '');

  return (
    getDraftErrorCode(error) === '8462005' ||
    /draft version conflict/i.test(message) ||
    message.includes('\u8349\u7a3f\u7248\u672c\u51b2\u7a81') ||
    message.includes('\u6700\u65b0\u8349\u7a3f')
  );
};

const isPendingDraftExistsError = (error: unknown) => {
  const code = getDraftErrorCode(error);
  const message =
    error instanceof Error
      ? error.message
      : String((error as { message?: string } | null | undefined)?.message || error || '');

  return (
    code === 'DRAFT_EXISTS' ||
    /pending draft/i.test(message) ||
    message.includes('\u5f85\u786e\u8ba4\u7684\u8349\u7a3f') ||
    message.includes('\u73b0\u6709\u8349\u7a3f')
  );
};

export const saveSelectedElementDraft = async (
  userProjectId: number,
  selectedElement: SelectedElementInfo,
  directUpdates: ElementContentUpdates,
  changeNote: string,
  options?: {
    suppressErrorToast?: boolean;
    retries?: number;
    minBaseVersion?: number | null;
  },
): Promise<WebpageDraft> => {
  const maxRetries = Math.max(1, options?.retries ?? 3);
  let attempt = 0;

  while (attempt < maxRetries) {
    attempt += 1;
    const resolution = await resolveEditableDraft(userProjectId);
    const currentDraft = resolution.editableDraft || resolution.pendingReview?.currentDraft;
    const baseVersionCandidates = [
      options?.minBaseVersion,
      resolution.saveBaseVersion,
      currentDraft?.version,
    ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    const baseVersion = baseVersionCandidates.length ? Math.max(...baseVersionCandidates) : null;

    if (!currentDraft?.content) {
      throw new Error('Current draft is unavailable.');
    }

    const updatedContent = updateElementInHtml(
      currentDraft.content,
      selectedElement.id,
      directUpdates,
      {
        textTargetId: selectedElement.textTargetId || selectedElement.id,
        srcTargetId: selectedElement.srcTargetId,
        hrefTargetId: selectedElement.hrefTargetId,
      },
    );

    try {
      return await projectApi.saveDraft(
        {
          userProjectId,
          content: updatedContent,
          changeNote,
          draftStatus: 'CONFIRMED',
          baseVersion: baseVersion ?? currentDraft.version,
        },
        { suppressErrorToast: options?.suppressErrorToast ?? true },
      );
    } catch (error) {
      if (
        attempt >= maxRetries ||
        (!isDraftVersionConflictError(error) && !isPendingDraftExistsError(error))
      ) {
        throw error;
      }

      await sleep(180 * attempt);
    }
  }

  throw new Error('Failed to save selected element draft.');
};

export const detectUnexpectedGlobalLanguageDrift = (
  command: string,
  beforeHtml: string,
  afterHtml: string,
): UnexpectedGlobalLanguageDrift | null => {
  if (!String(command || '').includes(SELECTED_ELEMENT_TARGETED_EDIT_MARKER)) return null;

  const beforeTexts = extractMeaningfulTextNodes(beforeHtml);
  const afterTexts = extractMeaningfulTextNodes(afterHtml);
  const totalTextCount = Math.max(beforeTexts.length, afterTexts.length);

  if (totalTextCount < MIN_DRIFT_TEXT_NODE_COUNT) {
    return null;
  }

  const beforeLanguage = inferDominantLanguageFromText(beforeTexts.join(' '));
  const afterLanguage = inferDominantLanguageFromText(afterTexts.join(' '));
  const supportedLanguageFlip =
    (beforeLanguage === 'English' && afterLanguage === 'Chinese') ||
    (beforeLanguage === 'Chinese' && afterLanguage === 'English');

  if (!supportedLanguageFlip) {
    return null;
  }

  const changedTextCount = countChangedTextNodes(beforeTexts, afterTexts);
  const changedTextRatio = changedTextCount / totalTextCount;

  if (
    changedTextCount < MIN_CHANGED_TEXT_NODE_COUNT ||
    changedTextRatio < MIN_CHANGED_TEXT_NODE_RATIO
  ) {
    return null;
  }

  return {
    beforeLanguage,
    afterLanguage,
    changedTextCount,
    totalTextCount,
  };
};
